/**
 * 本機存檔：高度圖＋題記＋物件＋配方＋投放＋目擊。
 */

import { terrainBuffer } from '../core/terrainBuffer'
import { HEIGHTMAP_SIZE } from '../core/terrainConstants'
import type { MapLabel, PlacedObject } from '../core/store/gameStore'
import type { Placement, Sighting, Species } from './types'

const DB_NAME = 'shanhaijing-world'
const DB_VER = 1
const STORE = 'world'

export interface WorldSave {
  worldId: string
  heightmap: ArrayBuffer
  /** 舊島檔可能帶植被密度圖；讀入後丟棄，不再寫回 */
  vegetation?: ArrayBuffer
  placedObjects: PlacedObject[]
  mapLabels: MapLabel[]
  species: Species[]
  placements: Placement[]
  sightings: Sighting[]
  savedAt: string
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export function snapshotTerrain(): { heightmap: ArrayBuffer } {
  return {
    heightmap: terrainBuffer.heightmap.buffer.slice(0) as ArrayBuffer,
  }
}

export function applyTerrain(save: WorldSave) {
  const h = new Float32Array(save.heightmap)
  if (h.length === HEIGHTMAP_SIZE * HEIGHTMAP_SIZE * 4) {
    terrainBuffer.heightmap.set(h)
    terrainBuffer.heightmapVersion++
    terrainBuffer.getHeightVersion++
  }
  terrainBuffer.ready = true
}

export async function writeWorldSave(save: WorldSave): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(save, 'current')
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

export async function readWorldSave(): Promise<WorldSave | null> {
  const db = await openDb()
  const save = await new Promise<WorldSave | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get('current')
    req.onsuccess = () => resolve((req.result as WorldSave) ?? null)
    req.onerror = () => reject(req.error)
  })
  db.close()
  return save
}

export function downloadWorldPack(save: WorldSave) {
  const blob = new Blob([JSON.stringify({
    worldId: save.worldId,
    heightmap: arrayBufferToBase64(save.heightmap),
    placedObjects: save.placedObjects,
    mapLabels: save.mapLabels,
    species: save.species,
    placements: save.placements,
    sightings: save.sightings,
    savedAt: save.savedAt,
  })], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `我的山海-${save.worldId.slice(0, 8)}.json`
  a.click()
  URL.revokeObjectURL(a.href)
}

export async function parseWorldPack(file: File): Promise<WorldSave> {
  const raw = JSON.parse(await file.text()) as Omit<WorldSave, 'heightmap'> & { heightmap: string }
  return {
    worldId: raw.worldId,
    heightmap: base64ToArrayBuffer(raw.heightmap),
    placedObjects: raw.placedObjects,
    mapLabels: raw.mapLabels,
    species: raw.species,
    placements: raw.placements,
    sightings: raw.sightings,
    savedAt: raw.savedAt,
  }
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(bin)
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}
